(() => {
  const SHIPPED = new Set(['CARRIER_PICKED_UP', 'IN_TRANSIT', 'OUT_FOR_DELIVERY', 'ATTEMPTED_DELIVERY', 'DELAYED', 'DELIVERED']);
  class SimpsonHubOrders {
    constructor(hub) {
      this.hub = hub; this.view = hub.querySelector('[data-hub-view="orders"]');
      this.root = this.view?.querySelector('[data-hub-orders-api]');
      this.copy = JSON.parse(hub.querySelector('[data-hub-orders-copy]')?.textContent || '{}');
      this.reset();
      hub.addEventListener('click', event => {
        if (event.target.closest('[data-hub-orders-more]')) this.fill(true);
        if (event.target.closest('[data-hub-orders-retry]')) this.failedDetail ? this.open(this.failedDetail) : this.fill();
        const card = event.target.closest('[data-hub-api-order]');
        if (card) this.open(card.dataset.hubApiOrder);
        const copy = event.target.closest('[data-hub-copy-tracking]');
        if (copy) this.copyTracking(copy);
      }, { signal: hub.listeners.signal });
      hub.account?.ready.then(() => this.activate(hub.currentView));
      this.expireReturnActions();
      document.addEventListener('visibilitychange', () => this.expireReturnActions(), { signal: hub.listeners.signal });
    }
    reset() {
      this.epoch = (this.epoch || 0) + 1; this.orders = new Map(); this.after = null;
      this.more = true; this.started = false; this.active = false; this.filterName = 'all'; this.limit = 20;
      this.cursors = new Set();
      this.loading = null;
      this.opening = false; this.failedDetail = null; this.statusKey = '';
      if (this.root) { this.root.hidden = true; this.root.querySelector('[data-hub-orders-list]').replaceChildren(); }
      this.view?.querySelector('[data-hub-orders-fallback]')?.removeAttribute('hidden');
      this.hub.querySelectorAll('[data-hub-api-detail]').forEach(node => node.remove());
    }
    t(key) { return this.copy[key] || key.replaceAll('_', ' '); }
    accountInterrupted(error) {
      const account = this.hub.account;
      if (account.connected() || account.loggingOut || account.dead) return false;
      account.controls();
      if (this.hub.currentView === 'orders') account.status(error.key === 'account_mismatch' || error.message === 'account_mismatch' ? 'account_mismatch' : 'session_expired', [], 'orders');
      return true;
    }
    node(tag, text, css) { const node = document.createElement(tag); if (text != null) node.textContent = text; if (css) node.className = css; return node; }
    icon(name) { return this.hub.querySelector(`[data-hub-order-icon="${name}"]`)?.content.firstElementChild.cloneNode(true) || this.node('span'); }
    carrier(company) {
      const carrier = (company || '').trim().toLowerCase();
      const badge = this.node('span', null, 'customer-hub__carrier-badge'); badge.setAttribute('aria-hidden', 'true');
      // Carrier identity comes from Shopify, never from a guessed tracking-number prefix.
      const name = /^ups(?:\s|$)|^united parcel service(?:\s|$)/.test(carrier) ? 'ups'
        : /^usps(?:\s|$)|^united states postal service(?:\s|$)/.test(carrier) ? 'usps' : null;
      const logo = name && this.hub.querySelector(`[data-hub-carrier-logo="${name}"]`)?.content.firstElementChild;
      if (logo) { badge.dataset.carrier = name; badge.append(logo.cloneNode(true)); }
      else badge.append(this.icon('shipping'));
      return badge;
    }
    async copyTracking(button) {
      const status = button.closest('.customer-hub__tracking-card').querySelector('[data-hub-tracking-feedback]');
      try { await navigator.clipboard.writeText(button.dataset.hubCopyTracking); status.textContent = this.t('tracking_copied'); }
      catch { status.textContent = this.t('copy_failed'); }
    }
    milestones(order, shipment) {
      const events = (shipment.events?.nodes || []).filter(event => Number.isFinite(new Date(event.happenedAt).valueOf())).sort((a,b) => new Date(a.happenedAt) - new Date(b.happenedAt));
      const transit = new Set(['IN_TRANSIT', 'OUT_FOR_DELIVERY', 'ATTEMPTED_DELIVERY', 'DELAYED', 'DELIVERED']);
      const groups = [null, SHIPPED, transit, new Set(['DELIVERED'])];
      const dateGroups = [null, new Set(['CARRIER_PICKED_UP']), new Set(['IN_TRANSIT']), new Set(['DELIVERED'])];
      return ['order_placed', 'shipped', 'in_transit', 'delivered'].map((key, index) => {
        const matched = index ? events.find(event => groups[index].has(event.status)) : null;
        const dated = index ? events.find(event => dateGroups[index].has(event.status)) : null;
        return { key, complete: index === 0 || !!matched || groups[index].has(shipment.latestShipmentStatus), date: index === 0 ? order.createdAt || order.processedAt : dated?.happenedAt };
      });
    }
    timeline(order, shipment) {
      const list = this.node('ol', null, 'customer-hub__timeline'); list.setAttribute('aria-label', this.t('shipment_history'));
      const steps = this.milestones(order, shipment); const current = steps.reduce((last, step, index) => step.complete ? index : last, 0);
      for (const [index, step] of steps.entries()) {
        const node = this.node('li'); node.dataset.complete = String(step.complete);
        if (index === current) node.setAttribute('aria-current', 'step');
        node.append(this.node('span', '', 'customer-hub__timeline-dot'), this.node('span', this.t(step.key), 'customer-hub__timeline-label'));
        if (step.date) { const time = this.node('time', this.shortDate(step.date)); time.dateTime = step.date; node.append(time); }
        list.append(node);
      }
      return list;
    }
    shortDate(value) { const date = new Date(value); return Number.isNaN(date.valueOf()) ? '' : date.toLocaleDateString(this.hub.dataset.locale || undefined, { month: 'short', day: 'numeric' }); }
    async events(order, shipment) {
      const orderId = order.id.match(/^gid:\/\/shopify\/Order\/(\d+)$/)?.[1];
      const shipmentId = shipment.id.match(/^gid:\/\/shopify\/Fulfillment\/(\d+)$/)?.[1];
      if (!orderId || !shipmentId || !this.orders.has(order.id)) throw Error('unknown_shipment');
      let after = null, itemsAfter = null, eventsMore = true, itemsMore = true;
      const nodes = [], items = [], seen = new Set(), itemCursors = new Set();
      do {
        const customer = this.verify((await this.hub.account.graphql('shipmentEvents', { orderQuery: `id:${orderId}`, shipmentQuery: `id:${shipmentId}`, after, itemsAfter })).customer);
        const result = customer.orders.nodes.find(item => item.id === order.id)?.fulfillments.nodes.find(item => item.id === shipment.id);
        if (!result) throw Error('unknown_shipment');
        if (eventsMore) { nodes.push(...result.events.nodes); after = this.cursor(result.events, seen); eventsMore = !!after; }
        if (itemsMore) { items.push(...result.fulfillmentLineItems.nodes); itemsAfter = this.cursor(result.fulfillmentLineItems, itemCursors); itemsMore = !!itemsAfter; }
      } while (eventsMore || itemsMore);
      return { events: { nodes }, items };
    }
    async loadEvents(order, epoch) {
      for (const shipment of this.shipments(order)) {
        try { const detail = await this.events(order, shipment); shipment.events = detail.events; shipment.items = detail.items; shipment.eventsFailed = false; shipment.itemsFailed = detail.items.some(item => !Number.isInteger(item.quantity) || item.quantity < 0); }
        catch { shipment.eventsFailed = true; shipment.itemsFailed = true; }
        if (epoch !== this.epoch) return;
      }
    }
    money(value) { return value ? new Intl.NumberFormat(this.hub.dataset.locale || undefined, { style: 'currency', currency: value.currencyCode }).format(Number(value.amount)) : ''; }
    date(value) { const date = new Date(value); return Number.isNaN(date.valueOf()) ? '' : date.toLocaleDateString(this.hub.dataset.locale || undefined, { year: 'numeric', month: 'long', day: 'numeric' }); }
    url(value) { try { const url = new URL(value); return url.protocol === 'https:' ? url.href : null; } catch { return null; } }
    shipments(order) { return order.fulfillments.nodes.filter(item => item.requiresShipping && !['CANCELLED', 'ERROR', 'FAILURE'].includes(item.status)); }
    state(order) {
      const shipments = this.shipments(order);
      const delivered = order.fulfillmentStatus === 'FULFILLED' && !order.fulfillments.pageInfo.hasNextPage && shipments.length > 0 && shipments.every(item => item.latestShipmentStatus === 'DELIVERED');
      return { delivered, shipped: !delivered && shipments.some(item => SHIPPED.has(item.latestShipmentStatus)),
        processing: !order.cancelledAt && ['UNFULFILLED', 'IN_PROGRESS', 'PARTIALLY_FULFILLED', 'SCHEDULED', 'ON_HOLD'].includes(order.fulfillmentStatus),
        refunded: ['REFUNDED', 'PARTIALLY_REFUNDED'].includes(order.financialStatus) || Number(order.totalRefunded?.amount) > 0 };
    }
    matches(order) { return this.filterName === 'all' || this.state(order)[this.filterName]; }
    label(order) { const state = this.state(order); return this.t(order.cancelledAt ? 'cancelled' : state.delivered ? 'delivered' : state.shipped ? 'shipped' : (order.fulfillmentStatus || 'UNFULFILLED').toLowerCase()); }
    async activate(destination) { if (destination === 'orders' && (!this.hub.currentView || this.hub.currentView === destination) && this.root && this.hub.account?.connected() && !this.started) await this.fill(); }
    filter(button) {
      if (!this.active) return false;
      this.filterName = button.dataset.hubFilter; this.limit = 20;
      this.view.querySelectorAll('[data-hub-filter]').forEach(node => node.setAttribute('aria-pressed', String(node === button)));
      this.render(); this.fill(); return true;
    }
    verify(customer) {
      if (!customer || customer.id !== `gid://shopify/Customer/${this.hub.dataset.customerId}`) { this.hub.account.clearSession(); throw Error('account_mismatch'); }
      return customer;
    }
    cursor(connection, seen) {
      if (!connection.pageInfo.hasNextPage) return null;
      const cursor = connection.pageInfo.endCursor;
      if (!cursor || seen.has(cursor)) throw Error('pagination');
      seen.add(cursor); return cursor;
    }
    async detailPage(order, itemsAfter = null, shipmentsAfter = null) {
      const numeric = order.id.match(/^gid:\/\/shopify\/Order\/(\d+)$/)?.[1];
      if (!numeric || !this.orders.has(order.id)) throw Error('unknown_order');
      const customer = this.verify((await this.hub.account.graphql('orderDetail', { query: `id:${numeric}`, itemsAfter, shipmentsAfter })).customer);
      const result = customer.orders.nodes.find(item => item.id === order.id);
      if (!result) throw Error('unknown_order');
      return result;
    }
    async completeShipments(order) {
      let connection = order.fulfillments; const seen = new Set();
      while (connection.pageInfo.hasNextPage) {
        const after = this.cursor(connection, seen);
        connection = (await this.detailPage(order, null, after)).fulfillments;
        order.fulfillments.nodes.push(...connection.nodes); order.fulfillments.pageInfo = connection.pageInfo;
      }
    }
    async page(epoch) {
      const customer = this.verify((await this.hub.account.graphql('orders', { after: this.after })).customer);
      await this.acceptPage(customer, epoch);
    }
    async seed(customer) {
      if (this.started || !this.root) return;
      try { await this.acceptPage(this.verify(customer), this.epoch); this.root.hidden = false; this.message(''); this.render(); }
      catch { this.message('failed'); }
    }
    async acceptPage(customer, epoch) {
      if (epoch !== this.epoch) return;
      const connection = customer.orders;
      const next = this.cursor(connection, new Set(this.cursors));
      // Finish every package before classifying an order with split shipments.
      const previous = new Map(this.orders);
      try {
        for (const order of connection.nodes) {
          this.orders.set(order.id, order); await this.completeShipments(order);
          if (epoch !== this.epoch) return;
        }
      } catch (error) { if (epoch === this.epoch) this.orders = previous; throw error; }
      if (next) this.cursors.add(next);
      this.after = next; this.more = !!next; this.started = true; this.active = true;
    }
    async fill(increase = false) {
      if (!this.root || !this.hub.account?.connected()) return;
      if (this.loading) return this.loading;
      if (increase) this.limit += 20;
      this.failedDetail = null;
      const epoch = this.epoch;
      this.loading = (async () => {
        // Let the lock assignment finish even when every page is already cached.
        await Promise.resolve();
        if (epoch !== this.epoch) return;
        this.message('loading'); this.root.hidden = false;
        try {
          while (this.more && (!this.started || [...this.orders.values()].filter(order => this.matches(order)).length < this.limit)) {
            await this.page(epoch); if (epoch !== this.epoch) return; this.render();
          }
          this.message(''); if (this.hub.currentView === 'orders') this.hub.account.status('', [], 'orders');
        } catch (error) { if (!this.accountInterrupted(error) && epoch === this.epoch) this.message('failed'); }
        finally { if (epoch === this.epoch) { this.loading = null; this.render(); } }
      })();
      return this.loading;
    }
    message(key) {
      this.statusKey = key;
      const busy = ['loading', 'loading_details'].includes(key);
      const status = this.root.querySelector('[data-hub-orders-status]');
      status.textContent = key ? this.t(key) : '';
      status.setAttribute('data-loading', String(busy));
      this.root.setAttribute('aria-busy', String(busy));
      this.root.querySelector('[data-hub-orders-retry]').hidden = !['failed', 'details_failed'].includes(key);
      this.root.querySelector('[data-hub-orders-more]').disabled = busy;
      this.root.querySelectorAll('[data-hub-api-order]').forEach(node => { node.disabled = busy; });
      this.root.querySelector('[data-hub-orders-empty]').hidden = true;
    }
    render() {
      if (!this.active) return;
      this.view.querySelector('[data-hub-orders-fallback]').hidden = true;
      this.view.querySelector('[data-hub-shipment-unavailable]').hidden = true;
      this.view.querySelector('[data-hub-filter-empty]').hidden = true;
      const matches = [...this.orders.values()].filter(order => this.matches(order));
      const list = this.root.querySelector('[data-hub-orders-list]'); list.replaceChildren();
      for (const order of matches.slice(0, this.limit)) {
        const card = this.node('button', null, 'customer-hub__order-card'); card.type = 'button'; card.dataset.hubApiOrder = order.id; card.disabled = !!this.loading || this.opening;
        const heading = this.node('span', null, 'customer-hub__order-heading');
        heading.append(this.node('strong', order.name), this.node('span', this.label(order), 'customer-hub__status'));
        card.append(heading, this.node('span', `${this.date(order.processedAt)} · ${this.t((order.financialStatus || 'pending').toLowerCase())}`, 'customer-hub__muted'));
        const images = this.node('span', null, 'customer-hub__order-thumbnails'); images.setAttribute('aria-hidden', 'true');
        for (const item of order.lineItems.nodes) { const src = this.url(item.image?.url); if (src) { const image = this.node('img'); image.src = src; image.alt = ''; image.loading = 'lazy'; images.append(image); } }
        if (images.childElementCount) card.append(images);
        const amounts = this.node('span', null, 'customer-hub__order-amounts');
        for (const [key, value] of [['total', order.totalPrice], ['refunded_amount', order.totalRefunded]]) {
          if (!value || (key === 'refunded_amount' && !(Number(value.amount) > 0))) continue;
          const row = this.node('span', null, 'customer-hub__order-amount-row');
          row.append(this.node('span', this.t(key)), this.node('strong', this.money(value)));
          amounts.append(row);
        }
        card.append(amounts); list.append(card);
      }
      const empty = this.root.querySelector('[data-hub-orders-empty]'); empty.hidden = matches.length > 0 || this.more || !!this.loading || !!this.statusKey;
      empty.textContent = this.t(['shipped', 'delivered'].includes(this.filterName) ? 'no_reported_shipments' : this.filterName === 'all' ? 'no_orders' : 'empty');
      this.root.querySelector('[data-hub-orders-more]').hidden = !this.more && matches.length <= this.limit;
    }
    tracking(order) {
      const region = this.node('div'); region.dataset.hubLiveTracking = '';
      const shipments = this.shipments(order);
      if (order.cancelledAt && !shipments.length) {
        region.append(this.node('p', this.t('cancelled_note'), 'customer-hub__muted'));
        return region;
      }
      if (!shipments.length) { if (order.requiresShipping) region.append(this.timeline(order, { latestShipmentStatus: null })); region.append(this.node('p', this.t('no_tracking'), 'customer-hub__muted')); }
      for (const [index, shipment] of shipments.entries()) {
        const group = this.node('article', null, 'customer-hub__shipment');
        const heading = this.node('div', null, 'customer-hub__shipment-heading');
        heading.append(this.node('h4', `${this.t('package')} ${index + 1}`), this.node('span', this.t((shipment.latestShipmentStatus || 'unknown_delivery').toLowerCase()), 'customer-hub__status'));
        group.append(heading, this.timeline(order, shipment));
        const card = this.node('div', null, 'customer-hub__tracking-card');
        if (!shipment.trackingInformation.length) card.append(this.node('p', this.t('tracking_unavailable'), 'customer-hub__muted'));
        for (const tracking of shipment.trackingInformation) {
          const row = this.node('div', null, 'customer-hub__tracking-heading'); const text = this.node('div');
          text.append(this.node('strong', tracking.company ? `${this.t('tracking_with')} ${tracking.company}` : this.t('shipping_details')));
          if (tracking.number) text.append(this.node('p', `${this.t('tracking_number')}: ${tracking.number}`, 'customer-hub__tracking-number'));
          row.append(this.carrier(tracking.company), text);
          if (tracking.number) { const copy = this.node('button', null, 'customer-hub__tracking-copy'); copy.type = 'button'; copy.dataset.hubCopyTracking = tracking.number; copy.setAttribute('aria-label', this.t('copy_tracking')); copy.append(this.icon('copy')); row.append(copy); }
          card.append(row);
          const url = this.url(tracking.url);
          if (url) { const link = this.node('a', this.t('track_package'), 'customer-hub__button'); link.href = url; link.target = '_blank'; link.rel = 'noopener noreferrer'; card.append(link); }
        }
        if (order.shippingLine?.title) card.append(this.node('p', `${this.t('shipping_method')}: ${order.shippingLine.title}`, 'customer-hub__muted customer-hub__shipping-method'));
        if (shipment.estimatedDeliveryAt && shipment.latestShipmentStatus !== 'DELIVERED') card.append(this.node('p', `${this.t('estimated_delivery')}: ${this.date(shipment.estimatedDeliveryAt)}`, 'customer-hub__muted'));
        const feedback = this.node('p', '', 'customer-hub__muted'); feedback.dataset.hubTrackingFeedback = ''; feedback.setAttribute('role', 'status'); card.append(feedback);
        group.append(card);
        if (shipment.eventsFailed) group.append(this.node('p', this.t('events_unavailable'), 'customer-hub__muted'));
        if (shipment.trackingInformation.length > 1) group.append(this.node('p', this.t('shared_tracking_items'), 'customer-hub__muted'));
        if (shipment.itemsFailed) group.append(this.node('p', this.t('shipment_items_unavailable'), 'customer-hub__muted'));
        else if (shipment.items?.length) group.append(this.itemGroup(order, shipment.items.map(item => ({...item.lineItem, shipmentQuantity: item.quantity})), this.t('items_in_shipment')));
        region.append(group);
      }
      return region;
    }
    async open(id) {
      const order = this.orders.get(id); if (!order || !this.hub.account.connected() || this.opening) return;
      this.opening = true; this.failedDetail = null; const epoch = this.epoch, view = this.hub.currentView;
      try {
        const numeric = id.split('/').pop(); this.message('loading_details');
        let detail = await this.detailPage(order); const seen = new Set(); let connection = detail.lineItems;
        while (connection.pageInfo.hasNextPage) {
          const after = this.cursor(connection, seen); connection = (await this.detailPage(order, after)).lineItems;
          detail.lineItems.nodes.push(...connection.nodes); detail.lineItems.pageInfo = connection.pageInfo;
        }
        if (epoch !== this.epoch || !this.hub.account.connected()) return;
        detail.fulfillments = order.fulfillments;
        await this.loadEvents(detail, epoch);
        if (epoch !== this.epoch || !this.hub.account.connected()) return;
        this.buildDetail(detail, numeric);
        if (this.hub.currentView === view && this.hub.drawer?.open !== false) this.hub.navigate(`api-order-${numeric}`);
        this.message('');
      } catch (error) {
        if (!this.accountInterrupted(error) && epoch === this.epoch) { this.failedDetail = id; this.message('details_failed'); this.hub.account.controls(); }
      }
      finally { if (epoch === this.epoch) { this.opening = false; this.root.querySelectorAll('[data-hub-api-order]').forEach(node => { node.disabled = false; }); } }
    }
    button(key, destination, icon) { const node = this.node('button', null, 'customer-hub__action-row'); node.type = 'button'; node.dataset.hubGo = destination; if (icon) node.append(this.icon(icon)); node.append(this.node('span', this.t(key)), this.node('span', '›')); return node; }
    returnExpiry(order) {
      const placed = new Date(order.createdAt).valueOf();
      return typeof order.createdAt === 'string' && Number.isFinite(placed) && placed <= Date.now() && Date.now() < placed + 30 * 86400000 ? (placed + 30 * 86400000) / 1000 : null;
    }
    returnPortal(order) { return this.returnExpiry(order) ? this.url(order.name.replace(/^#/, '').startsWith('23') ? this.root.dataset.legacyReturns : this.root.dataset.returns) : null; }
    expireReturnActions() {
      clearTimeout(this.returnTimer);
      const now = Date.now() / 1000; let next = Infinity;
      this.hub.querySelectorAll('[data-hub-return-expires]').forEach(node => {
        const expires = Number(node.dataset.hubReturnExpires);
        if (!Number.isFinite(expires) || expires <= now) node.hidden = true;
        else next = Math.min(next, expires);
      });
      if (Number.isFinite(next)) this.returnTimer = setTimeout(() => this.expireReturnActions(), Math.min(86400000, Math.max(1, (next - now) * 1000)));
    }
    itemGroup(order, items, label) {
      const group = this.node('details', null, 'customer-hub__items'); group.open = true;
      group.append(this.node('summary', `${label} (${items.length})`));
      for (const item of items) {
        const row = this.node('div', null, 'customer-hub__order-item'); const src = this.url(item.image?.url);
        if (src) { const image = this.node('img', null, 'customer-hub__product-image'); image.src = src; image.alt = item.title; image.loading = 'lazy'; row.append(image); }
        const text = this.node('div'); const heading = this.node('div', null, 'customer-hub__order-heading'); heading.append(this.node('strong', item.title));
        const qty = item.shipmentQuantity ?? item.quantity;
        if (qty === item.quantity && item.totalPrice) heading.append(this.node('strong', this.money(item.totalPrice)));
        else if (item.price) heading.append(this.node('span', `${this.money(item.price)} / ${this.t('each')}`));
        text.append(heading);
        if (item.variantTitle && item.variantTitle !== 'Default Title') text.append(this.node('p', item.variantTitle, 'customer-hub__muted'));
        text.append(this.node('p', `${this.t('quantity')}: ${qty}`, 'customer-hub__muted'));
        // Preserve only the availability-checked Liquid repurchase control. API-only items have no guessed eligibility.
        const numeric = item.id?.match(/\/(\d+)$/)?.[1]; const orderId = order.id.split('/').pop();
        const source = numeric && this.hub.querySelector(`[data-hub-view="order-${orderId}"] [data-hub-line-item-id="${numeric}"] [data-hub-buy-again]`);
        const actions = this.node('div', null, 'customer-hub__item-actions');
        if (source) { const button = source.cloneNode(true); button.disabled = false; button.removeAttribute('aria-busy'); actions.append(button); }
        const portal = this.returnPortal(order);
        if (portal) {
          const link = this.node('a', this.t('return_item'), 'customer-hub__button customer-hub__item-return');
          link.href = portal; link.target = '_blank'; link.rel = 'noopener noreferrer';
          link.dataset.hubReturnExpires = this.returnExpiry(order);
          link.setAttribute('aria-label', this.t('return_item_label').replace('__HUB_ITEM_TITLE__', item.title));
          link.append(this.node('span', ` ${this.t('opens_new_tab')}`, 'visually-hidden')); actions.append(link);
        }
        if (source) { const status = this.node('p'); status.dataset.hubAddStatus = ''; status.setAttribute('role', 'status'); actions.append(status); }
        if (actions.childElementCount) text.append(actions);
        // Review actions are intentionally absent until delivered-item and provider eligibility can both be verified.
        row.append(text); group.append(row);
      }
      return group;
    }
    remaining(order) {
      const shipments = this.shipments(order); const known = shipments.every(shipment => !shipment.itemsFailed && Array.isArray(shipment.items));
      const assigned = new Map();
      if (known) for (const shipment of shipments) for (const item of shipment.items) assigned.set(item.lineItem.id, (assigned.get(item.lineItem.id) || 0) + (item.quantity || 0));
      return { known, items: order.lineItems.nodes.map(item => ({...item, shipmentQuantity: known ? Math.max(0, item.quantity - (assigned.get(item.id) || 0)) : item.quantity})).filter(item => item.shipmentQuantity > 0) };
    }
    buildDetail(order, numeric) {
      const name = `api-order-${numeric}`;
      this.hub.querySelector(`[data-hub-view="${name}"]`)?.remove();
      const view = this.node('section', null, 'customer-hub__order-detail'); view.hidden = true; view.dataset.hubView = name; view.dataset.hubParent = 'orders'; view.dataset.hubNav = 'orders'; view.dataset.hubApiDetail = '';
      const header = this.node('div', null, 'customer-hub__detail-heading'); const heading = this.node('h3', this.t('order_title').replace('{{ name }}', order.name), 'customer-hub__view-title'); heading.tabIndex = -1;
      header.append(heading, this.node('span', this.label(order), 'customer-hub__status'));
      view.append(header, this.node('p', `${this.t('placed_on')} ${this.date(order.createdAt || order.processedAt)}`, 'customer-hub__muted customer-hub__placed-date'));
      if (this.state(order).refunded || order.cancelledAt) view.append(this.node('p', this.t((order.financialStatus || 'pending').toLowerCase()), 'customer-hub__financial-note'));
      view.append(this.tracking(order));
      const remaining = this.remaining(order);
      if (remaining.items.length) {
        const awaiting = remaining.known && remaining.items.every(item => item.requiresShipping !== false) && !order.cancelledAt && !this.state(order).refunded && ['UNFULFILLED', 'IN_PROGRESS', 'PARTIALLY_FULFILLED', 'ON_HOLD', 'SCHEDULED'].includes(order.fulfillmentStatus);
        if (!remaining.known) view.append(this.node('p', this.t('unassigned_note'), 'customer-hub__muted'));
        view.append(this.itemGroup(order, remaining.items, this.t(awaiting ? 'awaiting_shipment' : 'unassigned_items')));
      }
      const invoice = this.node('details', null, 'customer-hub__items customer-hub__invoice-details'); const summary = this.node('summary'); summary.append(this.icon('invoice'), this.node('span', this.t('invoice'))); invoice.append(summary);
      const refunded = this.state(order).refunded;
      const amounts = refunded ? [] : [['subtotal', order.subtotal], ['shipping', order.totalShipping], ['tax', order.totalTax], ['duties', order.totalDuties]];
      amounts.push(['total', order.totalPrice], ['refunded_amount', order.totalRefunded]);
      for (const [key, value] of amounts) {
        if (!value || (['duties', 'refunded_amount'].includes(key) && !(Number(value.amount) > 0))) continue;
        invoice.append(this.node('p', `${this.t(key)}: ${this.money(value)}`));
      }
      invoice.append(this.node('p', this.t(refunded ? 'refund_summary_note' : 'invoice_note'), 'customer-hub__muted'));
      const portal = this.returnPortal(order);
      const recentReturns = this.hub.querySelector(`[data-hub-view="returns-${numeric}"]`);
      if (portal && recentReturns) { recentReturns.dataset.hubParent = name; const button = this.button('start_return', `returns-${numeric}`, 'return'); button.dataset.hubReturnExpires = this.returnExpiry(order); view.append(button); }
      else if (portal) { const link = this.node('a', null, 'customer-hub__action-row'); link.append(this.icon('return'), this.node('span', this.t('start_return')), this.node('span', '›')); link.href = portal; link.target = '_blank'; link.rel = 'noopener noreferrer'; link.dataset.hubReturnExpires = this.returnExpiry(order); view.append(link); }
      view.append(invoice);
      const support = this.button('order_help', 'support', 'support'); support.dataset.hubOrderName = order.name; support.dataset.hubOrderParent = name; view.append(support);
      this.hub.querySelector('.customer-hub__content').append(view);
      this.expireReturnActions();
    }
    destroy() { clearTimeout(this.returnTimer); this.reset(); }
  }
  window.SimpsonHubOrders = SimpsonHubOrders;
})();
